import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export function NovelDescriptionInput() {
  const { t } = useTranslation();
  const { novel } = useNovel();
  const { updateNovelItem } = useNovelRepository();

  const [value, setValue] = useState(novel?.description || '');
  const [isEditing, setIsEditing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  const originalValue = useRef(novel?.description || '');
  const isCanceling = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Synchronize local input state with database value when not actively editing
  useEffect(() => {
    if (!isEditing) {
      setValue(novel?.description || '');
    }
  }, [novel?.description, isEditing]);

  if (!novel.id) return null;

  const handleFocus = () => {
    setIsEditing(true);
    originalValue.current = novel.description || '';
    if (status === 'saved') {
      setStatus('idle');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleBlur = async () => {
    if (isCanceling.current) {
      return;
    }

    setIsEditing(false);
    const trimmedValue = value.trim();

    if (trimmedValue !== (novel.description || '')) {
      try {
        setStatus('saving');
        await updateNovelItem({ description: trimmedValue });
        setStatus('saved');
      } catch (error) {
        console.error('Failed to update novel description:', error);
        setStatus('idle');
      }
    }
  };

  const handleCancel = () => {
    isCanceling.current = true;
    setValue(originalValue.current);
    setIsEditing(false);
    if (textareaRef.current) {
      textareaRef.current.blur();
    }
    // Clean up canceling flag after blur completes
    setTimeout(() => {
      isCanceling.current = false;
    }, 50);
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: { xs: '100%', sm: 300 } }}>
      <TextField
        inputRef={textareaRef}
        label={t('dashboard.cover.description_label')}
        placeholder={t('dashboard.cover.description_placeholder')}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        multiline
        fullWidth
        variant="outlined"
        sx={{
          flexGrow: 1,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            bgcolor: 'background.paper',
            height: '100%',
            '& fieldset': {
              borderColor: isEditing ? 'primary.main' : 'divider',
              borderWidth: isEditing ? 2 : 1,
            },
            '&:hover fieldset': {
              borderColor: 'primary.light',
            },
          },
          '& .MuiInputBase-input': {
            height: '100% !important',
            overflowY: 'auto !important',
          },
          '& .MuiInputLabel-root': {
            color: isEditing ? 'primary.main' : 'text.secondary',
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 0.75,
          minHeight: 24,
          px: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {status === 'saving' && (
            <>
              <CircularProgress size={12} thickness={5} sx={{ color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('dashboard.cover.saving')}
              </Typography>
            </>
          )}
          {status === 'saved' && (
            <>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'inline-block',
                }}
              />
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                {t('dashboard.cover.saved')}
              </Typography>
            </>
          )}
        </Box>

        {isEditing && (
          <Button
            variant="text"
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              handleCancel();
            }}
            sx={{
              py: 0,
              px: 1,
              minWidth: 0,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': {
                color: 'error.main',
                bgcolor: 'rgba(211, 47, 47, 0.04)',
              },
            }}
          >
            {t('dashboard.cover.cancel')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default NovelDescriptionInput;
